'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useCurrentUser } from './auth-provider';
import { useSessionStorage } from './use-session-storage';

export interface SelectedCustomer {
  customerNumber: string;
  customerName: string;
}

interface AdminState {
  /** User has the 'admin' role */
  isAdmin: boolean;
  /** Admin toggle: view all customers' data */
  allCustomers: boolean;
  setAllCustomers: (value: boolean) => void;
  /** Admin filter: selected customers with name + number */
  selectedCustomers: SelectedCustomer[];
  addCustomer: (customer: SelectedCustomer) => void;
  removeCustomer: (customerNumber: string) => void;
  clearCustomers: () => void;
  /** Just the customer numbers (for query variables) */
  selectedCustomerNumbers: string[];
  /** Ready-to-spread variables for GraphQL queries */
  adminQueryVars: {
    allCustomers?: boolean;
    customerNumbers?: string[];
  };
  /** Ready-to-spread variables for GraphQL mutations (singular customerNumber) */
  adminMutationVars: {
    customerNumber?: string;
  };
  /** Admin is viewing data for specific debitor(s) — cross-login active */
  isCrossLogin: boolean;
  /**
   * Exactly ONE coherent customer context is active — safe to surface
   * customer-owned state (Favoriten, Anfrageliste). True for normal customers,
   * for admins on their own data, and for cross-login with exactly one selected
   * debitor. False in „Alle Debitoren"/multi-select, where those queries return
   * aggregated or customer-mismatched data.
   */
  isSingleCustomerContext: boolean;
  /**
   * Build a context-aware heading like "Meine Belege", "Kunden Belege",
   * or "Kevin Sommer Belege" depending on admin state.
   */
  contextHeading: (subject: string) => string;
}

const AdminContext = createContext<AdminState | null>(null);

function extractLastName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1];
}

export function AdminProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const isAdmin = user?.roles?.includes('admin') ?? false;

  const [allCustomers, setAllCustomers] = useSessionStorage('crm-admin-allCustomers', false);
  const [selectedCustomers, setSelectedCustomers] = useSessionStorage<SelectedCustomer[]>('crm-admin-selectedCustomers', []);

  const selectedCustomerNumbers = useMemo(
    () => selectedCustomers.map((c) => c.customerNumber),
    [selectedCustomers]
  );

  const addCustomer = useCallback((customer: SelectedCustomer) => {
    setSelectedCustomers((prev) => {
      if (prev.some((c) => c.customerNumber === customer.customerNumber)) return prev;
      return [...prev, customer];
    });
  }, []);

  const removeCustomer = useCallback((customerNumber: string) => {
    setSelectedCustomers((prev) =>
      prev.filter((c) => c.customerNumber !== customerNumber)
    );
  }, []);

  const clearCustomers = useCallback(() => setSelectedCustomers([]), []);

  const adminQueryVars = useMemo(() => {
    if (!isAdmin || !allCustomers) return {};
    if (selectedCustomerNumbers.length > 0) {
      return { customerNumbers: selectedCustomerNumbers };
    }
    return { allCustomers: true };
  }, [isAdmin, allCustomers, selectedCustomerNumbers]);

  // For mutations: pass the single selected customer so writes go to the
  // right account. Only set when exactly one customer is selected.
  const adminMutationVars = useMemo(() => {
    if (!isAdmin || !allCustomers) return {};
    if (selectedCustomerNumbers.length === 1) {
      return { customerNumber: selectedCustomerNumbers[0] };
    }
    return {};
  }, [isAdmin, allCustomers, selectedCustomerNumbers]);

  const contextHeading = useCallback(
    (subject: string): string => {
      if (!isAdmin || !allCustomers) return `Meine ${subject}`;
      if (selectedCustomers.length === 0) return `Kunden ${subject}`;
      if (selectedCustomers.length === 1) {
        return `${selectedCustomers[0].customerName} ${subject}`;
      }
      // Multiple: "Nachname, Nachname, Nachname Belege"
      const lastNames = selectedCustomers.map((c) => extractLastName(c.customerName));
      return `${lastNames.join(', ')} ${subject}`;
    },
    [isAdmin, allCustomers, selectedCustomers]
  );

  const isCrossLogin = isAdmin && allCustomers && selectedCustomers.length >= 1;

  // Kunden-eigene Status (Favoriten, Anfrageliste) sind nur mit genau EINEM
  // Debitor-Kontext eindeutig: normaler Kunde, Admin auf eigenen Daten, oder
  // Cross-Login mit exakt einem gewählten Debitor. Bei „Alle Debitoren" bzw.
  // Mehrfachauswahl liefern die Queries Aggregat-/Fremd-Daten.
  const isSingleCustomerContext =
    !(isAdmin && allCustomers) || selectedCustomers.length === 1;

  const value = useMemo<AdminState>(
    () => ({
      isAdmin,
      allCustomers: isAdmin && allCustomers,
      setAllCustomers,
      selectedCustomers,
      selectedCustomerNumbers,
      addCustomer,
      removeCustomer,
      clearCustomers,
      adminQueryVars,
      adminMutationVars,
      isCrossLogin,
      isSingleCustomerContext,
      contextHeading,
    }),
    [
      isAdmin, allCustomers, selectedCustomers, selectedCustomerNumbers,
      addCustomer, removeCustomer, clearCustomers, adminQueryVars, adminMutationVars, isCrossLogin, isSingleCustomerContext, contextHeading,
    ]
  );

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
}

export function useAdmin(): AdminState {
  const ctx = useContext(AdminContext);
  if (!ctx) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return ctx;
}
